import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet, Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const dl = (blob, name) => {
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = u; a.download = name; a.click();
  URL.revokeObjectURL(u);
};

const STATUS_META = {
  ok: { cls: "text-emerald-600", Icon: CheckCircle2, label: "Hazır" },
  created: { cls: "text-emerald-600", Icon: CheckCircle2, label: "Oluşturuldu" },
  pending: { cls: "text-amber-600", Icon: AlertTriangle, label: "Onayda" },
  error: { cls: "text-red-600", Icon: XCircle, label: "Hata" },
};

export default function BulkUserImportDialog({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null); // {rows, created, failed, total, dry_run}

  const reset = () => { setFile(null); setData(null); };
  const close = () => { reset(); onClose?.(); };

  const getTemplate = async () => {
    try {
      const { data: blob } = await api.get("/users/bulk-import/template", { responseType: "blob" });
      dl(blob, "personel_sablonu.xlsx");
    } catch { toast.error("Şablon indirilemedi"); }
  };

  const run = async (dry) => {
    if (!file) return toast.error("Önce Excel dosyası seçin");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", dry ? "true" : "false");
      const { data: res } = await api.post("/users/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setData(res);
      if (!dry) { toast.success(`${res.created} kullanıcı oluşturuldu · ${res.failed} hata`); onDone?.(); }
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "İşlem başarısız"); }
    finally { setBusy(false); }
  };

  const exportCreds = async () => {
    try {
      const { data: blob } = await api.post("/users/bulk-import/export", { rows: data?.rows || [] }, { responseType: "blob" });
      dl(blob, "personel_giris_bilgileri.xlsx");
    } catch { toast.error("Dışa aktarılamadı"); }
  };

  const committed = data && !data.dry_run;
  const rows = data?.rows || [];

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="bulk-import-dialog" className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Toplu Personel Aktarımı</DialogTitle>
          <DialogDescription>Excel'den birden çok personel + kullanıcıyı tek seferde oluşturun. Sistem her satır için kullanıcı adı, şifre ve PIN üretir; sonucu Excel olarak indirebilirsiniz.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" data-testid="bulk-import-template-btn" onClick={getTemplate} className="border-brand-green text-brand-green hover:bg-emerald-50">
                <Download className="mr-1.5 h-4 w-4" /> Excel Şablonu İndir
              </Button>
              <label data-testid="bulk-import-file-label" className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                <FileSpreadsheet className="h-4 w-4 text-brand-green" />
                {file ? file.name : "Dosya Seç (.xlsx)"}
                <input data-testid="bulk-import-file" type="file" accept=".xlsx" className="hidden"
                  onChange={(e) => { setFile(e.target.files?.[0] || null); setData(null); }} />
              </label>
            </div>
            <p className="mt-2 text-[11px] text-slate-500">Şablondaki sütunlar: Ad Soyad*, Rol* (kod/etiket), E-posta, Telefon, TCKN, İşe Giriş, Lokasyon (ad/kod), Kademe, Kullanıcı Adı, Şifre, PIN, Aylık Maaş. Boş bırakılan kullanıcı adı/şifre/PIN sistem tarafından üretilir.</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="button" data-testid="bulk-import-preview-btn" disabled={busy || !file} onClick={() => run(true)} variant="outline" className="border-slate-300">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />} Önizle (Kontrol Et)
            </Button>
            <Button type="button" data-testid="bulk-import-commit-btn" disabled={busy || !file} onClick={() => run(false)} className="bg-brand-orange text-white hover:bg-orange-600">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />} İçe Aktar & Oluştur
            </Button>
            {committed && (
              <Button type="button" data-testid="bulk-import-export-btn" onClick={exportCreds} variant="outline" className="border-brand-green text-brand-green hover:bg-emerald-50">
                <Download className="mr-1.5 h-4 w-4" /> Giriş Bilgilerini İndir
              </Button>
            )}
          </div>

          {data && (
            <div data-testid="bulk-import-result">
              <div className="mb-2 flex flex-wrap gap-3 text-sm">
                <span className="rounded-md bg-slate-100 px-2.5 py-1 font-semibold text-slate-700">Toplam: {data.total}</span>
                {committed && <span className="rounded-md bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-700">Oluşturulan: {data.created}</span>}
                {data.failed > 0 && <span className="rounded-md bg-red-50 px-2.5 py-1 font-semibold text-red-700">Hata: {data.failed}</span>}
                {!committed && <span className="rounded-md bg-sky-50 px-2.5 py-1 font-semibold text-sky-700">Önizleme — henüz oluşturulmadı</span>}
              </div>
              <div className="max-h-72 overflow-auto rounded-lg border border-slate-200">
                <table className="w-full text-left text-sm">
                  <thead className="sticky top-0 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-400">
                    <tr>
                      <th className="px-3 py-2">#</th>
                      <th className="px-3 py-2">Ad</th>
                      <th className="px-3 py-2">Rol</th>
                      <th className="px-3 py-2">Durum</th>
                      {committed && <th className="px-3 py-2">Kullanıcı Adı</th>}
                      {committed && <th className="px-3 py-2">Şifre</th>}
                      {committed && <th className="px-3 py-2">PIN</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const m = STATUS_META[r.status] || STATUS_META.error;
                      return (
                        <tr key={i} data-testid={`bulk-import-row-${r.row}`} className="border-t border-slate-100">
                          <td className="px-3 py-1.5 text-slate-400">{r.row}</td>
                          <td className="px-3 py-1.5 font-medium text-slate-700">{r.name}</td>
                          <td className="px-3 py-1.5 text-slate-600">{r.role_label}</td>
                          <td className={`px-3 py-1.5 font-semibold ${m.cls}`}>
                            <span className="inline-flex items-center gap-1"><m.Icon className="h-3.5 w-3.5" /> {m.label}</span>
                            {r.error && <span className="ml-1 text-[11px] font-normal text-red-500">— {r.error}</span>}
                          </td>
                          {committed && <td className="px-3 py-1.5 font-mono text-slate-700">{r.username || "—"}</td>}
                          {committed && <td className="px-3 py-1.5 font-mono text-slate-700">{r.password || "—"}</td>}
                          {committed && <td className="px-3 py-1.5 font-mono tracking-widest text-slate-700">{r.pin || "—"}</td>}
                        </tr>
                      );
                    })}
                    {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-4 text-center text-slate-400">Dosyada geçerli satır bulunamadı.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
